import { useEffect, useState, useRef} from 'react';
import { ArrowButton } from "@components/shared/core/ArrowButton";
import cx from "classnames";
import { useTranslation } from "@services/i18n";
import { OBELIX_CAROUSEL_KEYS } from "@utils/obelixKeys";

const SimpleCarousel = ({
  slides
}: {
  slides: any[] | Element[];
}) => {
  const slider = useRef<HTMLDivElement>(null);
  const total = slides.length;
  const threshold = 50; // pixel distance of drag before triggering slider movement
  const slideGap = 10; // spacing between slides
  const { t } = useTranslation();
  const previousLabel = t(
    OBELIX_CAROUSEL_KEYS.previous.obelixKey,
    "Previous Slide"
  );
  const nextLabel = t(
    OBELIX_CAROUSEL_KEYS.next.obelixKey,
    "Next Slide"
  );

  const [current, setCurrent] = useState(0);
  const [sliderStyles, setSliderStyles] = useState({ 'left': '0' });
  const [controlsEnabled, setControlsEnabled] = useState({ prev: true, next: true});
  const [isDisabled, setIsDisabled] = useState(false);
  const [move, setMove] = useState({
    active: false,
    left: 0,
    startX: 0,
    dist: 0,
    thresholdReached: false
  });


  useEffect(() => {
    if (total <= 1) {
      setIsDisabled(true);

    } else {
      setIsDisabled(false);
      slideTo(current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  useEffect(() => {
    slideTo(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slides]);

  const updateMove = (newVals: any) => {
    setMove(prevVals => {
      return ({
        ...prevVals,
        ...newVals
      })
    });
  }

  const slideTo = (val: any) => {
    if (val === undefined) {
      val = current;
    }

    if (val === 'prev') {
      val = current - 1;
    }

    if (val === 'next') {
      val = current + 1;
    }

    if (val < 0) {
      val = 0;
    }

    if (val > total - 1) {
      val = total - 1;
    }

    setCurrent(val);

    // reset
    setControlsEnabled({ prev: true, next: true});

    if (val === 0) {
      setControlsEnabled({ prev: false, next: true });
    }

    if (val === total - 1) {
      setControlsEnabled({ prev: true, next: false });
    }

    let percent = val * -100;
    let gap = (val * slideGap) - val;

    if (percent > 0) {
      percent = 0;
      gap = 0;
    }

    setSliderStyles({ left: `calc(${percent}% - ${gap}px)` });
  };

  const onDragStart = (e: any) => {
    if (isDisabled) {
      return;
    }

    const touchEvt = e.touches ? e.touches[0] : e;
    let left = 0;

    if (slider.current && slider.current.style) {
      left = slider.current.offsetLeft;
    }

    updateMove({
      active: true,
      left: left,
      startX: touchEvt.clientX,
      dist: 0
    });
  };

  const onDrag = (e: any) => {
    if (isDisabled) {
      return;
    }

    if (move.active === true) {
      const touchEvt = e.touches ? e.touches[0] : e;

      updateMove({
        dist: touchEvt.clientX - move.startX
      });

      if (Math.abs(move.dist) > threshold || move.thresholdReached) {
        updateMove({ thresholdReached: true });
        setSliderStyles({ left: move.left + move.dist +'px' });
      }
    }
  };

  const onDragEnd = () => {
    if (move.active === false || isDisabled) {
      return;
    }

    updateMove({
      active: false,
      thresholdReached: false
    });

    let newVal = current;

    if (move.dist < -threshold) {
      setCurrent(newVal++);

      if (newVal > total - 1) {
        setCurrent(total - 1);
      }

    } else if (move.dist > threshold) {
      setCurrent(newVal--);

      if (newVal < 0) {
        setCurrent(0);
      }
    }

    slideTo(newVal);
  };

  return (
    <div className="w-full h-full relative rounded-xl overflow-hidden">
      <div
        className="select-none relative w-full overflow-hidden"
        onTouchStart={onDragStart}
        onTouchMove={onDrag}
        onTouchEnd={onDragEnd}
        onMouseDown={onDragStart}
        onMouseMove={onDrag}
        onMouseUp={onDragEnd}
        onMouseLeave={onDragEnd}
      >
        <div
          ref={slider}
          className={cx('relative w-full flex flex-row flex-nowrap justify-between top-0 left-0 transition-[left] duration-300 ease-in-out', move.thresholdReached && 'transition-none', !isDisabled && 'cursor-grab active:cursor-grabbing')}
          style={{
            columnGap: `${slideGap}px`,
            ...sliderStyles
          }}
        >
          {slides.map((slide, i) => {
            return (
              <div key={i} className={cx('w-full select-none shrink-0 rounded-xl overflow-hidden', move.thresholdReached && 'pointer-events-none')}>
                {slide}
              </div>
            )
          })}
        </div>
      </div>
      {!isDisabled && (
        <div className="flex w-full justify-center gap-[10px] dsk:m-0 mt-[10px]">
          <ArrowButton
            disabled={!controlsEnabled.prev}
            onClick={() => slideTo('prev')}
            className="relative rotate-180 size-12 dsk:absolute dsk:top-[calc(50%-8px)] dsk:size-10 z-[100] dsk:left-[32px]"
            ariaLabel={previousLabel}
          />
          <ArrowButton
            disabled={!controlsEnabled.next}
            onClick={() => slideTo('next')}
            className="relative size-12 dsk:absolute dsk:top-[calc(50%-8px)] dsk:size-10 z-[100] dsk:right-[32px]"
            ariaLabel={nextLabel}
          />
        </div>
      )}
    </div>
  );
}

export default SimpleCarousel;